"use client";

import { useCallback, useRef, useSyncExternalStore } from "react";

type ScopedStateListener = () => void;

type ScopedStateEntry<TState> = {
  value: TState;
  listeners: Set<ScopedStateListener>;
  notifyScheduled: boolean;
};

type ScopedStateStoreBucket = Map<string, ScopedStateEntry<unknown>>;

const scopedStateStoreKey = "__phiScopedStateStores__";

function normalizeScopedStateKey(scopeKey: string) {
  return scopeKey.trim() || "default";
}

function getScopedStateStoreMap() {
  const globalStore = globalThis as typeof globalThis & {
    [scopedStateStoreKey]?: Map<string, ScopedStateStoreBucket>;
  };

  if (!globalStore[scopedStateStoreKey]) {
    globalStore[scopedStateStoreKey] = new Map<string, ScopedStateStoreBucket>();
  }

  return globalStore[scopedStateStoreKey];
}

export function createScopedStateStore<TState>(
  storeId: string,
  createDefaultState: (scopeKey: string) => TState,
) {
  const hydrationSnapshots = new Map<string, TState>();

  function getHydrationSnapshot(scopeKey: string) {
    const normalizedScopeKey = normalizeScopedStateKey(scopeKey);
    let snapshot = hydrationSnapshots.get(normalizedScopeKey);

    if (snapshot === undefined) {
      snapshot = createDefaultState(normalizedScopeKey);
      hydrationSnapshots.set(normalizedScopeKey, snapshot);
    }

    return snapshot;
  }

  function getScopedBucket() {
    const map = getScopedStateStoreMap();
    let bucket = map.get(storeId);

    if (!bucket) {
      bucket = new Map<string, ScopedStateEntry<unknown>>();
      map.set(storeId, bucket);
    }

    return bucket;
  }

  function getScopedEntry(scopeKey: string) {
    const normalizedScopeKey = normalizeScopedStateKey(scopeKey);
    const bucket = getScopedBucket();
    let entry = bucket.get(normalizedScopeKey) as ScopedStateEntry<TState> | undefined;

    if (!entry) {
      entry = {
        value: createDefaultState(normalizedScopeKey),
        listeners: new Set<ScopedStateListener>(),
        notifyScheduled: false,
      };
      bucket.set(normalizedScopeKey, entry);
    }

    return entry;
  }

  function subscribe(scopeKey: string, listener: ScopedStateListener) {
    const entry = getScopedEntry(scopeKey);
    entry.listeners.add(listener);
    return () => {
      entry.listeners.delete(listener);
    };
  }

  function getSnapshot(scopeKey: string) {
    return getScopedEntry(scopeKey).value;
  }

  function patch(scopeKey: string, updater: (current: TState) => TState) {
    const entry = getScopedEntry(scopeKey);
    const nextValue = updater(entry.value);
    if (Object.is(entry.value, nextValue)) {
      return;
    }
    entry.value = nextValue;

    if (entry.notifyScheduled) {
      return;
    }

    entry.notifyScheduled = true;
    queueMicrotask(() => {
      entry.notifyScheduled = false;
      for (const listener of entry.listeners) {
        listener();
      }
    });
  }

  function replace(scopeKey: string, nextState: TState) {
    const entry = getScopedEntry(scopeKey);
    if (Object.is(entry.value, nextState)) {
      return;
    }
    entry.value = nextState;

    if (entry.notifyScheduled) {
      return;
    }

    entry.notifyScheduled = true;
    queueMicrotask(() => {
      entry.notifyScheduled = false;
      for (const listener of entry.listeners) {
        listener();
      }
    });
  }

  function useStore(scopeKey: string) {
    const normalizedScopeKey = normalizeScopedStateKey(scopeKey);
    /*
     * Held per scope. An inline `subscribe` is a new function every render, and `useSyncExternalStore`
     * unsubscribes and subscribes again for each new one.
     */
    const subscribeToScope = useCallback(
      (listener: ScopedStateListener) => subscribe(normalizedScopeKey, listener),
      [normalizedScopeKey],
    );
    return useSyncExternalStore(
      subscribeToScope,
      () => getSnapshot(normalizedScopeKey),
      () => getHydrationSnapshot(normalizedScopeKey),
    );
  }

  function useStoreSelector<TSelected>(
    scopeKey: string,
    selector: (state: TState) => TSelected,
  ) {
    const normalizedScopeKey = normalizeScopedStateKey(scopeKey);
    /*
     * Cached on the source and on the selector. Keyed on the source alone, a selector that changed
     * while the store did not -- a different id, a different scope -- kept answering with what the
     * previous one selected. An inline selector is new each render, so it runs once per render; within
     * one render every read agrees, which is what `useSyncExternalStore` needs.
     */
    const selectedSnapshotRef = useRef<{
      source: TState;
      selector: (state: TState) => TSelected;
      selected: TSelected;
    } | null>(null);
    const selectedHydrationSnapshotRef = useRef<{
      source: TState;
      selector: (state: TState) => TSelected;
      selected: TSelected;
    } | null>(null);
    const getSelectedSnapshot = () => {
      const source = getSnapshot(normalizedScopeKey);
      const cached = selectedSnapshotRef.current;
      if (cached && Object.is(cached.source, source) && cached.selector === selector) {
        return cached.selected;
      }

      const selected = selector(source);
      selectedSnapshotRef.current = { source, selector, selected };
      return selected;
    };
    const getSelectedHydrationSnapshot = () => {
      const source = getHydrationSnapshot(normalizedScopeKey);
      const cached = selectedHydrationSnapshotRef.current;
      if (cached && Object.is(cached.source, source) && cached.selector === selector) {
        return cached.selected;
      }

      const selected = selector(source);
      selectedHydrationSnapshotRef.current = { source, selector, selected };
      return selected;
    };

    const subscribeToScope = useCallback(
      (listener: ScopedStateListener) => subscribe(normalizedScopeKey, listener),
      [normalizedScopeKey],
    );
    return useSyncExternalStore(
      subscribeToScope,
      getSelectedSnapshot,
      getSelectedHydrationSnapshot,
    );
  }

  function reset(scopeKey: string) {
    patch(scopeKey, () => createDefaultState(normalizeScopedStateKey(scopeKey)));
  }

  /*
   * Forgets a scope's state. A component still mounted on it keeps its subscription on the entry it
   * subscribed to, so dropping that entry left it listening to nothing: the next write went into a new
   * entry nobody was told about. With listeners, the entry stays and starts over from the default
   * instead, and they hear it.
   */
  function deleteScope(scopeKey: string) {
    const normalizedScopeKey = normalizeScopedStateKey(scopeKey);
    const bucket = getScopedBucket();
    const entry = bucket.get(normalizedScopeKey) as ScopedStateEntry<TState> | undefined;
    if (entry && entry.listeners.size > 0) {
      replace(normalizedScopeKey, createDefaultState(normalizedScopeKey));
      return;
    }
    bucket.delete(normalizedScopeKey);
  }

  return {
    useStore,
    useStoreSelector,
    subscribe,
    getSnapshot,
    /** The stable value to render during hydration, so server and client agree on the first paint. */
    getHydrationSnapshot,
    patch,
    replace,
    reset,
    deleteScope,
  };
}
